import Phaser from 'phaser';
import { SCENE_KEYS, LIVES_CONFIG, ENERGY_CONFIG, GAME_CONFIG, designModeState } from '@/config/constants';
import { AchievementManager } from '@/utils/AchievementManager';
import { AudioManager } from '@/utils/AudioManager';
import { AudioChoreographer } from '@/utils/AudioChoreographer';
import { COLORS, COLORS_HEX, FONTS } from '@/ui/theme';
import { NumberRoller } from '@/utils/NumberRoller';
import { PostFXManager } from '@/utils/PostFXManager';
import { ParticlePools } from '@/utils/ParticlePools';
import { UltimateArc } from '@/utils/UltimateArc';
// UIAnimations available for future use

// ─── CHUNKY CARTOON HUD PALETTE ──────────────────────────────────────────
// Bold, high-contrast pill kit inspired by Crash Bandicoot / Overcooked.
// Solid yellow pills with thick navy borders, green stars, green/blue bars.
const HUD_YELLOW = 0xF5C73C;
const HUD_YELLOW_HEX = '#F5C73C';
const HUD_YELLOW_LIGHT = 0xFFE38A;
const HUD_YELLOW_SHADOW = 0xC79A1F;
const HUD_NAVY = 0x0C1A2E;
const HUD_NAVY_HEX = '#0C1A2E';
const HUD_NAVY_DEEP = 0x05101D;
const HUD_GREEN = 0x3FD24A;
const HUD_GREEN_LIGHT = 0x7CE89A;
const HUD_BLUE = 0x7DD3FC;
const HUD_BLUE_LIGHT = 0xC9EEFD;

/**
 * UI Scene - HUD Overlay
 * Clean slate for building UI elements from scratch
 */
export class UIScene extends Phaser.Scene {
  private scoreText?: Phaser.GameObjects.Text;
  private timerText?: Phaser.GameObjects.Text;
  private timerRingBg?: Phaser.GameObjects.Graphics;
  private timerRingFill?: Phaser.GameObjects.Graphics;
  private comboBanner?: Phaser.GameObjects.Image;
  private statusLabelText?: Phaser.GameObjects.Text;
  private hudLayout: 'standard' | 'boss' = 'standard';
  private startingTimeLimit = 99;

  // Lives display - chunky green stars (one per remaining "continue").
  private lifeStars: Phaser.GameObjects.Graphics[] = [];
  private currentLives: number = LIVES_CONFIG.MAX_LIVES;

  // ── TRIPLE-AAA: ENERGY bar (per-life HP) ──
  // One energy bar per life. Hits drain energy; depleting the bar consumes
  // one life and refills the bar for a retry. 3 lives = 3 continues total.
  private currentEnergy: number = ENERGY_CONFIG.MAX_ENERGY;
  private energyBarBg?: Phaser.GameObjects.Graphics;
  private energyBarFill?: Phaser.GameObjects.Graphics;
  private energyBarGlow?: Phaser.GameObjects.Graphics;
  private energyPulseTween?: Phaser.Tweens.Tween;

  // Combo display
  private comboText?: Phaser.GameObjects.Text;
  private comboTimer?: Phaser.Time.TimerEvent;
  private ultimateArc?: UltimateArc;

  // Scoring system
  private score = 0;
  private displayedScore = 0; // For rolling counter animation
  private scoreRoller?: NumberRoller;
  private currentStreakSize: number | null = null;
  private streakCount = 0;

  // Ball counter
  private ballCountText?: Phaser.GameObjects.Text;

  // Educational "bags cleared" secondary counter - only rendered when the
  // current stage has stageConfig.educational.itemTheme. Two text nodes
  // (big number + small label) sit below the main ball counter so the
  // player reads: "1" (bubbles remaining) → "15 BAGS CLEARED" underneath.
  private bagsClearedText?: Phaser.GameObjects.Text;
  private bagsClearedLabel?: Phaser.GameObjects.Text;
  private educationalItemLabel?: string;

  // Timer system
  private timeRemaining = 99;
  private timerEvent?: Phaser.Time.TimerEvent;

  // Demo mode stored for future use

  constructor() {
    super({ key: SCENE_KEYS.UI });
  }

  init(data?: { startingScore?: number; lives?: number; demoMode?: boolean; layout?: 'standard' | 'boss' }): void {
    const startingScore = data?.startingScore || 0;
    this.score = startingScore;
    this.currentLives = data?.lives ?? LIVES_CONFIG.MAX_LIVES;
    this.hudLayout = data?.layout ?? 'standard';
    // Always start a stage at full energy. The UIScene instance is reused
    // across scene.launch cycles so the class-field initializer for
    // currentEnergy only runs once at boot. Without this reset the player
    // would inherit the energy they ended the previous stage with.
    this.currentEnergy = ENERGY_CONFIG.MAX_ENERGY;
    // demoMode available via data?.demoMode for future use
    console.log(`📊 UI scene initialized with starting score: ${startingScore}, lives: ${this.currentLives}, energy: ${this.currentEnergy}/${ENERGY_CONFIG.MAX_ENERGY}`);
  }

  create(): void {
    console.log('📊 UI scene created - clean slate');

    // Clear stale Text references from the previous scene run. Phaser
    // destroys the display list on shutdown, but class-field references
    // survive and still point at the destroyed Text objects. If we leave
    // these set, setEducationalTheme() takes the "reuse existing text"
    // branch and setText() throws inside Phaser's canvas drawImage - that
    // throw propagates out of GameScene.handleBallHit()'s tween onStart
    // and freezes the ball mid-split.
    this.bagsClearedText = undefined;
    this.bagsClearedLabel = undefined;

    // Reset streak and timer (but keep score from init)
    this.currentStreakSize = null;
    this.streakCount = 0;
    this.startingTimeLimit = this.hudLayout === 'boss' ? 150 : 99;
    this.timeRemaining = this.startingTimeLimit;
    const { WIDTH } = GAME_CONFIG;

    // ═══════════════════════════════════════════════════════════════
    //  CHUNKY CARTOON HUD
    //   ┌─ SCORE ─────────┐                  ┌─ BUBBLES ───────┐
    //   │     10,000      │                  │       20        │
    //   └─────────────────┘                  └─────────────────┘
    //   LIVES  ★ ★ ★                                TIME  ━━━━━━━
    //   ENERGY ━━━━━━━━━━━
    //  Solid yellow pills + thick navy borders, green stars,
    //  green energy bar, blue time bar. No textures - pure code.
    // ═══════════════════════════════════════════════════════════════

    const SCORE_PILL_W = 340;
    const BUBBLES_PILL_W = 220;
    const PILL_H = 115;
    const PILL_TOP = 22;
    const PILL_LEFT_X = 36;
    const PILL_RIGHT_X = WIDTH - 36 - BUBBLES_PILL_W;
    const PILL_RADIUS = 28;

    const ROW_Y_1 = PILL_TOP + PILL_H + 30; // first row under pills (LIVES / TIME)
    const ROW_Y_2 = ROW_Y_1 + 38;            // second row under LIVES (ENERGY)
    const LABEL_PAD_X = 6;
    const ROW_LABEL_GAP = 14;
    const BAR_H = 18;

    const isBoss = this.hudLayout === 'boss';

    // ─── SCORE pill (top-left) ───
    this.drawChunkyPill(PILL_LEFT_X, PILL_TOP, SCORE_PILL_W, PILL_H, PILL_RADIUS);
    const scoreCx = PILL_LEFT_X + SCORE_PILL_W / 2;
    this.add.text(scoreCx, PILL_TOP + 18, 'SCORE', {
      fontFamily: FONTS.displayLight, fontSize: '20px',
      color: HUD_NAVY_HEX, letterSpacing: 1,
    } as any).setOrigin(0.5, 0).setDepth(101);
    this.scoreText = this.add.text(scoreCx, PILL_TOP + 54, this.score.toLocaleString('en-US'), {
      fontFamily: FONTS.displayLight, fontSize: '44px',
      color: HUD_NAVY_HEX,
    }).setOrigin(0.5, 0).setDepth(101);
    this.scoreRoller = new NumberRoller(this, this.scoreText);

    // ─── BUBBLES pill (top-right, narrower) - or HYDRA in boss mode ───
    const rightLabel = isBoss ? 'HYDRA' : 'BUBBLES';
    const rightValue = isBoss ? 'ARMORED' : '0';
    const rightValueSize = isBoss ? '26px' : '44px';
    const rightValueY = isBoss ? PILL_TOP + 62 : PILL_TOP + 54;

    this.drawChunkyPill(PILL_RIGHT_X, PILL_TOP, BUBBLES_PILL_W, PILL_H, PILL_RADIUS);
    const bubblesCx = PILL_RIGHT_X + BUBBLES_PILL_W / 2;
    this.statusLabelText = this.add.text(bubblesCx, PILL_TOP + 18, rightLabel, {
      fontFamily: FONTS.displayLight, fontSize: '20px',
      color: HUD_NAVY_HEX, letterSpacing: 1,
    } as any).setOrigin(0.5, 0).setDepth(101);
    this.ballCountText = this.add.text(bubblesCx, rightValueY, rightValue, {
      fontFamily: FONTS.displayLight, fontSize: rightValueSize,
      color: HUD_NAVY_HEX,
    }).setOrigin(0.5, 0).setDepth(101);

    // ─── Shared label style for LIVES / ENERGY / TIME row labels ───
    const rowLabelStyle = {
      fontFamily: FONTS.displayLight, fontSize: '20px',
      color: '#FFFFFF',
      stroke: HUD_NAVY_HEX, strokeThickness: 4,
      letterSpacing: 1,
      shadow: { offsetX: 0, offsetY: 2, color: HUD_NAVY_HEX, blur: 0, fill: true },
    } as any;

    // ─── LIVES row (under SCORE pill) - label + green life stars ───
    this.add.text(PILL_LEFT_X + LABEL_PAD_X, ROW_Y_1, 'LIVES', rowLabelStyle)
      .setOrigin(0, 0.5).setDepth(101);
    this.lifeStars = [];

    // ─── ENERGY row (under LIVES) ───
    const energyLabelW = 84;
    this.add.text(PILL_LEFT_X + LABEL_PAD_X, ROW_Y_2, 'ENERGY', rowLabelStyle)
      .setOrigin(0, 0.5).setDepth(101);
    const energyBarX = PILL_LEFT_X + LABEL_PAD_X + energyLabelW + ROW_LABEL_GAP;
    const energyBarRightLimit = PILL_LEFT_X + SCORE_PILL_W;
    const energyBarW = energyBarRightLimit - energyBarX;
    this.buildEnergyBar(energyBarX, ROW_Y_2, energyBarW, BAR_H);

    // ─── LIVES stars (every stage) ───
    // Surface the player's remaining lives as green stars on the LIVES row,
    // aligned to the start of the energy bar below so the two rows read as a
    // pair. The boss draws its own copy of these (the shared HUD sits behind
    // the full-screen arena there), so this set covers the normal stages.
    {
      const starSize = 26;
      const starGap = 12;
      for (let i = 0; i < LIVES_CONFIG.MAX_LIVES; i++) {
        const star = this.add.graphics().setDepth(101);
        star.setPosition(energyBarX + starSize / 2 + i * (starSize + starGap), ROW_Y_1);
        star.setData('starSize', starSize);
        this.repaintLifeStar(star, i < this.currentLives, starSize);
        this.lifeStars.push(star);
      }
    }

    // ─── TIME row (under BUBBLES pill - but extend bar leftward so it has room) ───
    // BUBBLES pill is narrow; the TIME bar can extend further left to match SCORE side.
    const TIME_BLOCK_W = 280; // total width of "TIME" label + bar
    const timeBlockRight = PILL_RIGHT_X + BUBBLES_PILL_W;
    const timeBlockLeft = timeBlockRight - TIME_BLOCK_W;
    const timeLabel = this.add.text(timeBlockLeft, ROW_Y_1, 'TIME', rowLabelStyle)
      .setOrigin(0, 0.5).setDepth(101);
    const timeLabelW = 60;
    const timeBarX = timeBlockLeft + timeLabelW + ROW_LABEL_GAP;
    const timeBarW = timeBlockRight - timeBarX;
    (this as any)._timerBarX = timeBarX;
    (this as any)._timerBarY = ROW_Y_1;
    (this as any)._timerBarW = timeBarW;
    (this as any)._timerBarH = BAR_H;

    this.timerRingBg = this.add.graphics().setDepth(100);
    this.timerRingFill = this.add.graphics().setDepth(101);
    this.drawTimerBar(1.0);

    // The boss fight is untimed (see startTimer), so hide the whole TIME row
    // for the boss layout - a frozen, full timer bar would otherwise read as a
    // countdown the player is racing against.
    if (isBoss) {
      timeLabel.setVisible(false);
      this.timerRingBg.setVisible(false);
      this.timerRingFill.setVisible(false);
    }

    // The numeric timer no longer renders in the HUD - the bar communicates
    // remaining time. Keep the Text alive offscreen so the existing urgency
    // tweens (color flash / scale punch) still target a valid object.
    this.timerText = this.add.text(-9999, -9999, this.timeRemaining.toString(), {
      fontFamily: FONTS.displayHeavy, fontSize: '32px', color: HUD_NAVY_HEX,
    }).setOrigin(0.5).setDepth(102).setVisible(false);

    // ─── COMBO (floats centred at top of arena) ───
    this.comboText = this.add.text(WIDTH / 2, PILL_TOP + PILL_H + 36, '', {
      fontFamily: FONTS.displayHeavy, fontSize: '44px', color: HUD_YELLOW_HEX,
      stroke: HUD_NAVY_HEX, strokeThickness: 8,
      shadow: { offsetX: 0, offsetY: 6, color: HUD_NAVY_HEX, blur: 0, fill: true },
      letterSpacing: 4,
    } as any).setOrigin(0.5).setDepth(200).setAlpha(0);

    if (isBoss) {
      this.updateBossStatus('ARMORED', 'HYDRA', HUD_NAVY_HEX);
    }

    // ── TRIPLE-AAA: Overwatch-style screen-edge ULTIMATE ARC ──────────
    // Fills as combo grows - full circuit at x8 (POSEIDON'S WRATH ready).
    this.ultimateArc = new UltimateArc(this);

    // Listen for stage gameplay score events only when the standard arena is running.
    if (this.hudLayout === 'standard') {
      const gameScene = this.scene.get(SCENE_KEYS.GAME);
      if (gameScene) {
        gameScene.events.off('ball-destroyed', this.onBallDestroyed, this);
        gameScene.events.on('ball-destroyed', this.onBallDestroyed, this);
      }
    }

    // Start timer countdown
    this.startTimer();
  }

  public updateBossStatus(value: string, label: string = 'HYDRA', color: string = COLORS_HEX.aquaLight): void {
    this.statusLabelText?.setText(label);

    if (this.ballCountText) {
      this.ballCountText.setText(value);
      this.ballCountText.setColor(color);
      this.ballCountText.setAlpha(1);
    }
  }

  /**
   * Handle ball destroyed event from GameScene
   * Converts ball radius to size (1-4) and adds score
   */
  private comboDecayTimer?: Phaser.Time.TimerEvent;

  private onBallDestroyed(data: { radius: number; score2x?: boolean }): void {
    const size = this.radiusToBubbleSize(data.radius);
    this.addBubbleScore(size, data.score2x || false);
  }

  /**
   * Convert ball radius to bubble size (1-4)
   * Sizes are based on split progression with 0.6 ratio
   */
  private radiusToBubbleSize(radius: number): number {
    // Ball splits with 0.6 ratio from starting radius of 128
    // Size 4 (Largest): ~128 radius
    // Size 3 (Large): ~77 radius (128 * 0.6)
    // Size 2 (Medium): ~46 radius (77 * 0.6)
    // Size 1 (Smallest): ~28 radius (46 * 0.6)
    
    if (radius >= 100) return 4; // Largest
    if (radius >= 60) return 3;  // Large
    if (radius >= 38) return 2;  // Medium
    return 1; // Smallest
  }

  /**
   * Add score for destroying a bubble with streak multiplier
   * @param bubbleSize The size of the bubble (1-4, where 1 is smallest)
   */
  public addBubbleScore(bubbleSize: number, score2x: boolean = false): void {
    // Base points for each bubble size
    const basePoints: { [key: number]: number } = {
      4: 50,   // Largest
      3: 100,  // Large
      2: 150,  // Medium
      1: 200,  // Smallest
    };

    const points = basePoints[bubbleSize] || 0;

    // Check if we're continuing a streak or starting a new one
    if (this.currentStreakSize === bubbleSize) {
      // Continue streak
      this.streakCount++;
    } else {
      // New streak started
      this.currentStreakSize = bubbleSize;
      this.streakCount = 1;
    }

    // Calculate multiplier based on streak count
    let multiplier = 1;
    if (this.streakCount === 1) {
      multiplier = 1; // Base points
    } else if (this.streakCount === 2) {
      multiplier = 2; // x2
    } else if (this.streakCount === 3) {
      multiplier = 4; // x4
    } else if (this.streakCount >= 4) {
      multiplier = 8; // x8
    }

    let earnedPoints = points * multiplier;
    if (score2x) earnedPoints *= 2; // Double points powerup
    this.score += earnedPoints;

    // Reset combo decay timer (lose combo after 3 seconds of no hits)
    if (this.comboDecayTimer) this.comboDecayTimer.remove();
    this.comboDecayTimer = this.time.delayedCall(3000, () => {
      this.resetStreak();
      if (this.comboText) {
        this.tweens.add({ targets: this.comboText, alpha: 0, duration: 300 });
      }
      if (this.comboBanner) {
        this.tweens.add({ targets: this.comboBanner, alpha: 0, duration: 260 });
      }
    });

    // Update display
    this.updateScoreDisplay();

    // Add visual feedback based on points earned
    this.scoreVisualFeedback(earnedPoints);

    // Emit event back to GameScene to show floating score text on bubble
    // Only show multiplier if > 1 (x2, x3, x4, x8)
    const gameScene = this.scene.get('GameScene');
    if (gameScene) {
      gameScene.events.emit('show-score-text', {
        points: earnedPoints,
        multiplier: multiplier > 1 ? multiplier : null, // null = don't show
        basePoints: points
      });
    }

    // AAA: Play combo escalation sound (ascending pitch)
    if (multiplier > 1) {
      const audioManager = AudioManager.getInstance();
      audioManager.playComboSFX(this, this.streakCount);
    }

    // AAA: Combo milestone stinger + banner
    const choreo = AudioChoreographer.get(this);
    const milestone = choreo.checkComboMilestone(this.streakCount);
    if (milestone) {
      this.showComboMilestoneBanner(milestone, this.streakCount);
      // Oceanic Overwatch: Poseidon voice on combo milestones (x5 mid, x8+ mega)
      const am = AudioManager.getInstance();
      if (this.streakCount >= 8) {
        am.playVoiceStinger(this, 'combo_mega', 3000);
      } else if (this.streakCount >= 5) {
        am.playVoiceStinger(this, 'combo_mid', 2000);
      }
    }

    // ── ULTIMATE ARC - fill proportional to streak toward x8 ──
    this.ultimateArc?.setProgress(Math.min(1, this.streakCount / 4));

    // AAA: COMBO FINISHER ULTIMATE at x8 - Poseidon's Wrath visual
    if (multiplier >= 8) {
      this.ultimateArc?.flashFull();
      this.triggerComboFinisher();
    }

    // Update combo display
    this.updateComboDisplay(multiplier);

    // Achievement triggers
    const achievements = AchievementManager.getInstance();
    achievements.setScene(this);
    achievements.unlock('first_pop');
    if (multiplier >= 4) achievements.unlock('combo_x4');
    if (multiplier >= 8) achievements.unlock('combo_x8');
    if (this.score >= 5000) achievements.unlock('score_5000');
    if (this.score >= 20000) achievements.unlock('score_20000');

    console.log(`💰 Score: ${this.score} (+${earnedPoints}) | Streak: ${this.streakCount}x | Size: ${bubbleSize}`);
  }

  /**
   * Reset the streak multiplier (call when player takes damage or dies)
   */
  public resetStreak(): void {
    this.currentStreakSize = null;
    this.streakCount = 0;
    AudioChoreographer.get(this).resetComboMilestone();
    this.ultimateArc?.setProgress(0);
    console.log('🔄 Streak reset');
  }

  // ═══════════════════════════════════════════════════════════════════
  // TRIPLE-AAA: ENERGY BAR SYSTEM
  // Oceanic aqua crystal bar. 5 segments by default, one per life.
  // drainEnergy() is called from GameScene/BossScene on ball/attack hits.
  // When energy hits 0 → onEnergyDepleted() handles life consumption.
  // ═══════════════════════════════════════════════════════════════════

  private buildEnergyBar(x: number, y: number, w: number, h: number): void {
    // Background trough (drawn once)
    this.energyBarBg = this.add.graphics().setDepth(100);
    // Additive glow backplate
    this.energyBarGlow = this.add.graphics().setDepth(100).setBlendMode(Phaser.BlendModes.ADD);
    // Fill graphics (re-drawn on every change)
    this.energyBarFill = this.add.graphics().setDepth(101);

    // Stash geometry for redraw
    (this as any)._energyBarX = x;
    (this as any)._energyBarY = y;
    (this as any)._energyBarW = w;
    (this as any)._energyBarH = h;

    this.redrawEnergyBar();
  }

  private redrawEnergyBar(): void {
    if (!this.energyBarFill || !this.energyBarGlow || !this.energyBarBg) return;
    const fill = this.energyBarFill;
    const glow = this.energyBarGlow;
    const bg = this.energyBarBg;
    fill.clear();
    glow.clear();
    bg.clear();

    const x: number = (this as any)._energyBarX;
    const y: number = (this as any)._energyBarY;
    const w: number = (this as any)._energyBarW;
    const h: number = (this as any)._energyBarH;
    const top = y - h / 2;
    const cornerR = h / 2; // pill

    // ── Background trough (dark navy, navy stroke) ──
    bg.fillStyle(HUD_NAVY_DEEP, 1);
    bg.fillRoundedRect(x, top, w, h, cornerR);
    bg.lineStyle(4, HUD_NAVY, 1);
    bg.strokeRoundedRect(x, top, w, h, cornerR);

    const ratio = ENERGY_CONFIG.MAX_ENERGY > 0
      ? Math.max(0, this.currentEnergy / ENERGY_CONFIG.MAX_ENERGY)
      : 0;
    const isDanger = this.currentEnergy <= ENERGY_CONFIG.LOW_ENERGY_THRESHOLD;
    const isCrit = this.currentEnergy <= 1;

    if (ratio > 0) {
      const fillColor = isCrit ? COLORS.dangerRed
        : isDanger ? COLORS.warningAmber
        : HUD_GREEN;
      const sheenColor = isCrit ? 0xFFE0E0
        : isDanger ? 0xFFF4C2
        : HUD_GREEN_LIGHT;

      const innerPad = 3;
      const innerX = x + innerPad;
      const innerTop = top + innerPad;
      const innerH = h - innerPad * 2;
      const innerW = (w - innerPad * 2) * ratio;
      const innerR = innerH / 2;
      const drawW = Math.max(innerH, innerW); // never narrower than the rounded cap

      // Solid fill
      fill.fillStyle(fillColor, 1);
      fill.fillRoundedRect(innerX, innerTop, drawW, innerH, innerR);
      // Top sheen highlight
      fill.fillStyle(sheenColor, 0.85);
      fill.fillRoundedRect(innerX + 4, innerTop + 2, Math.max(0, drawW - 8), Math.max(2, innerH * 0.32), innerR);

      // Additive glow halo
      glow.fillStyle(fillColor, 0.45);
      glow.fillRoundedRect(innerX - 2, innerTop - 2, drawW + 4, innerH + 4, innerR + 1);
    }

    // Pulse the bar when critical
    if (isCrit && !this.energyPulseTween) {
      this.energyPulseTween = this.tweens.add({
        targets: [this.energyBarGlow],
        alpha: { from: 1, to: 0.4 },
        duration: 280,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else if (!isCrit && this.energyPulseTween) {
      this.energyPulseTween.stop();
      this.energyPulseTween = undefined;
      if (this.energyBarGlow) this.energyBarGlow.setAlpha(1);
    }
  }

  /**
   * Called by GameScene / BossScene when the player takes a hit.
   * Returns true if the hit depleted energy (i.e. a life should be consumed).
   */
  public drainEnergy(amount: number = ENERGY_CONFIG.DAMAGE_PER_HIT): boolean {
    const before = this.currentEnergy;
    this.currentEnergy = Math.max(0, this.currentEnergy - amount);
    this.redrawEnergyBar();

    // Shake the bar on hit
    if (this.energyBarFill && this.energyBarGlow) {
      const bar = [this.energyBarFill, this.energyBarGlow];
      this.tweens.add({
        targets: bar,
        x: { from: -4, to: 0 },
        duration: 60,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.easeInOut',
      });
    }

    // PostFX punch on each hit
    try {
      const gs = this.scene.get(SCENE_KEYS.GAME) as Phaser.Scene | undefined;
      PostFXManager.get(gs ?? this)?.pulseDanger(280, Math.min(1, (ENERGY_CONFIG.MAX_ENERGY - this.currentEnergy) / ENERGY_CONFIG.MAX_ENERGY + 0.3));
    } catch {}

    const depleted = before > 0 && this.currentEnergy === 0;
    return depleted;
  }

  /** Refill energy to max (called after a life is consumed for retry). */
  public refillEnergy(): void {
    this.currentEnergy = ENERGY_CONFIG.MAX_ENERGY;
    this.redrawEnergyBar();
  }

  public getEnergy(): number {
    return this.currentEnergy;
  }

  /**
   * AAA v2: Combo milestone banner with ESCALATING TIER VISUAL IDENTITY.
   *
   *   COMBO   (x3)  → yellow, small banner, coral sparks, brief zoom
   *   GREAT   (x5)  → orange, screen-edge pulse, zoom 1.02, air-horn
   *   PERFECT (x10) → rainbow gradient, split-word reveal, shockwaves, slo-mo
   *   GODLIKE (x20) → FULL BOSS-INTRO TREATMENT: letterbox, arena freeze,
   *                   camera orbit, choir swell, time-slice
   */
  private showComboMilestoneBanner(label: string, combo: number): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;

    // Client's gaming-words pass: each tier renders with the player-facing
    // copy below, while the underlying `label` still drives tier styling +
    // audio routing. Add new tiers here to localise the on-screen text.
    const DISPLAY_LABEL: Record<string, string> = {
      COMBO:   'COMBO x3!',
      GREAT:   'ON FIRE!',
      PERFECT: 'TIDAL WAVE!',
      GODLIKE: 'LEGENDARY!',
    };
    const displayLabel = DISPLAY_LABEL[label] || label;

    const tiers: Record<string, {
      main: string; stroke: string; glow: string;
      fontSize: number; shockwaves: number; zoomPunch: number;
      particles: 'gold' | 'sapphire' | 'coral' | 'pearl';
      letterbox: boolean; slowMo: number;
    }> = {
      COMBO:   { main: '#FEF08A', stroke: '#5B3900', glow: '#FACC15', fontSize: 140, shockwaves: 1, zoomPunch: 0.01, particles: 'gold',     letterbox: false, slowMo: 0 },
      GREAT:   { main: '#FDBA74', stroke: '#7C2D12', glow: '#EA580C', fontSize: 165, shockwaves: 2, zoomPunch: 0.02, particles: 'coral',    letterbox: false, slowMo: 0 },
      PERFECT: { main: '#FFFFFF', stroke: '#7C3AED', glow: '#F0ABFC', fontSize: 210, shockwaves: 4, zoomPunch: 0.04, particles: 'pearl',    letterbox: false, slowMo: 200 },
      GODLIKE: { main: '#FFF4C2', stroke: '#581C0D', glow: '#FFD700', fontSize: 260, shockwaves: 6, zoomPunch: 0.06, particles: 'gold',     letterbox: true,  slowMo: 450 },
    };
    const t = tiers[label] || tiers.COMBO;

    // ─── CAMERA JUICE (zoom punch, shake, optional slo-mo) ─────────
    const gameScene = this.scene.get(SCENE_KEYS.GAME);
    if (gameScene) {
      gameScene.cameras.main.zoomTo(1 + t.zoomPunch, 140, 'Back.easeOut');
      this.time.delayedCall(320, () => gameScene.cameras.main.zoomTo(1, 280, 'Quad.easeOut'));
      if (t.slowMo > 0) {
        gameScene.time.timeScale = 0.4;
        this.time.delayedCall(t.slowMo, () => { gameScene.time.timeScale = 1; });
      }
    }

    // ─── POSTFX COMBO SHIMMER ──────────────────────────────────────
    try {
      const shimmerMgr = PostFXManager.get(gameScene ?? this);
      shimmerMgr?.setComboShimmer(Math.min(1, combo / 15));
      if (label === 'PERFECT' || label === 'GODLIKE') shimmerMgr?.ultFlash(label === 'GODLIKE' ? 900 : 500);
    } catch {}

    // ─── LETTERBOX BARS (GODLIKE only) ─────────────────────────────
    let topBar: Phaser.GameObjects.Rectangle | undefined;
    let botBar: Phaser.GameObjects.Rectangle | undefined;
    if (t.letterbox) {
      const h = 80;
      topBar = this.add.rectangle(0, -h, WIDTH, h, 0x000000, 0.95).setOrigin(0, 0).setDepth(9996);
      botBar = this.add.rectangle(0, HEIGHT, WIDTH, h, 0x000000, 0.95).setOrigin(0, 0).setDepth(9996);
      this.tweens.add({ targets: topBar, y: 0, duration: 220, ease: 'Back.easeOut' });
      this.tweens.add({ targets: botBar, y: HEIGHT - h, duration: 220, ease: 'Back.easeOut' });
    }

    // ─── EXPANDING SHOCKWAVE RINGS (color-tiered) ──────────────────
    const ringTint = parseInt(t.glow.replace('#', '0x'));
    for (let i = 0; i < t.shockwaves; i++) {
      this.time.delayedCall(i * 90, () => {
        const ring = this.add.circle(cx, cy - 40, 60, 0x000000, 0).setStrokeStyle(6, ringTint, 1);
        ring.setDepth(9998).setBlendMode(Phaser.BlendModes.ADD);
        this.tweens.add({
          targets: ring,
          scale: 14 + i * 3,
          alpha: 0,
          duration: 650,
          ease: 'Cubic.easeOut',
          onComplete: () => ring.destroy(),
        });
      });
    }

    // ─── GLOW BACKPLATE (additive blurred text) ────────────────────
    const glow = this.add.text(cx, cy - 60, displayLabel, {
      fontFamily: 'CF Asty Std',
      fontSize: `${t.fontSize}px`,
      color: t.glow,
      stroke: t.glow,
      strokeThickness: 22,
      fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(9999).setAlpha(0).setBlendMode(Phaser.BlendModes.ADD);

    // ─── MAIN LABEL with split-word reveal for PERFECT/GODLIKE ─────
    const main = this.add.text(cx, cy - 60, displayLabel, {
      fontFamily: 'CF Asty Std',
      fontSize: `${t.fontSize}px`,
      color: t.main,
      stroke: t.stroke,
      strokeThickness: 14,
      fontStyle: 'italic',
      shadow: { offsetX: 0, offsetY: 8, color: '#000000', blur: 22, fill: true },
    }).setOrigin(0.5).setDepth(10000).setAlpha(0).setScale(0.2);

    // PERFECT/GODLIKE get a gradient fill via canvas texture
    if (label === 'PERFECT' || label === 'GODLIKE') {
      try {
        const c = main.context as CanvasRenderingContext2D;
        const grad = c.createLinearGradient(0, 0, 0, main.height);
        if (label === 'PERFECT') {
          grad.addColorStop(0, '#C084FC'); grad.addColorStop(0.5, '#F0ABFC'); grad.addColorStop(1, '#7C3AED');
        } else {
          grad.addColorStop(0, '#FFF4C2'); grad.addColorStop(0.5, '#FFD700'); grad.addColorStop(1, '#B8892C');
        }
        main.setTint(0xFFFFFF);
        (main.style as any).setFill(grad);
        main.updateText();
      } catch {}
    }

    // Sub-label "xN COMBO"
    const subText = this.add.text(cx, cy + 80, `x${combo} COMBO`, {
      fontFamily: 'CF Asty Std',
      fontSize: label === 'GODLIKE' ? '96px' : '72px',
      color: '#FFFFFF',
      stroke: t.stroke,
      strokeThickness: 8,
      fontStyle: 'italic',
      shadow: { offsetX: 0, offsetY: 6, color: '#000000', blur: 18, fill: true },
      letterSpacing: 4,
    } as any).setOrigin(0.5).setDepth(10000).setAlpha(0).setScale(0.3);

    // ─── ENTRANCE ──────────────────────────────────────────────────
    this.tweens.add({ targets: [main, glow], alpha: 1, scale: 1, duration: 260, ease: 'Back.easeOut', easeParams: [3] });
    this.tweens.add({ targets: subText, alpha: 1, scale: 1, duration: 320, delay: 120, ease: 'Back.easeOut', easeParams: [2.5] });

    // Pulsing glow
    this.tweens.add({ targets: glow, alpha: { from: 0.9, to: 0.25 }, duration: 220, yoyo: true, repeat: 2, delay: 240, ease: 'Sine.easeInOut' });

    // ─── PARTICLE SPRAY (color-matched to tier) ────────────────────
    try {
      const count = label === 'GODLIKE' ? 120 : label === 'PERFECT' ? 70 : label === 'GREAT' ? 40 : 24;
      ParticlePools.get(this).burst(t.particles, cx, cy, count);
      if (label === 'GODLIKE' || label === 'PERFECT') {
        ParticlePools.get(this).burst('gold', cx, cy - 80, 30);
        ParticlePools.get(this).burst('pearl', cx + 200, cy, 20);
        ParticlePools.get(this).burst('pearl', cx - 200, cy, 20);
      }
    } catch {}

    // ─── EXIT ──────────────────────────────────────────────────────
    const holdMs = label === 'GODLIKE' ? 1700 : label === 'PERFECT' ? 1300 : 1100;
    this.time.delayedCall(holdMs, () => {
      this.tweens.add({
        targets: [main, glow, subText],
        alpha: 0,
        scale: 1.35,
        y: '-=60',
        duration: 420,
        ease: 'Power2',
        onComplete: () => { main.destroy(); glow.destroy(); subText.destroy(); },
      });
      if (topBar && botBar) {
        this.tweens.add({ targets: topBar, y: -80, duration: 320, ease: 'Power2', onComplete: () => topBar!.destroy() });
        this.tweens.add({ targets: botBar, y: HEIGHT, duration: 320, ease: 'Power2', onComplete: () => botBar!.destroy() });
      }
      // Fade combo shimmer out
      try {
        const shimmerMgr = PostFXManager.get(gameScene ?? this);
        this.tweens.add({ targets: { s: Math.min(1, combo / 15) }, s: 0, duration: 800, onUpdate: (tw) => shimmerMgr?.setComboShimmer((tw.targets[0] as any).s) });
      } catch {}
    });
  }

  /**
   * Get current score
   */
  public getScore(): number {
    return this.score;
  }

  /**
   * Get time remaining (in seconds)
   */
  public getTimeRemaining(): number {
    return this.timeRemaining;
  }

  /**
   * Update combo multiplier display
   */
  private updateComboDisplay(multiplier: number): void {
    if (!this.comboText) return;

    if (multiplier > 1) {
      this.comboText.setText(`x${multiplier} COMBO!`);
      this.comboText.setAlpha(1);
      this.comboBanner?.setAlpha(1);

      // Color based on multiplier level
      const colors: Record<number, string> = {
        2: '#FFD700', // Gold
        4: '#FF6600', // Orange
        8: '#FF2222', // Red hot
      };
      this.comboText.setColor(colors[multiplier] || '#FFD700');

      // Pop animation
      this.tweens.add({
        targets: this.comboText,
        scale: { from: 1.4, to: 1.0 },
        duration: 200,
        ease: 'Back.easeOut',
      });
      if (this.comboBanner) {
        this.tweens.add({
          targets: this.comboBanner,
          scaleX: { from: this.comboBanner.scaleX * 0.92, to: this.comboBanner.scaleX },
          scaleY: { from: this.comboBanner.scaleY * 0.92, to: this.comboBanner.scaleY },
          duration: 220,
          ease: 'Back.easeOut',
        });
      }

      // Auto-hide after 2 seconds of no hits
      if (this.comboTimer) this.comboTimer.remove();
      this.comboTimer = this.time.delayedCall(2000, () => {
        this.tweens.add({
          targets: this.comboText,
          alpha: 0,
          duration: 300,
        });
        if (this.comboBanner) {
          this.tweens.add({
            targets: this.comboBanner,
            alpha: 0,
            duration: 260,
          });
        }
      });
    }
  }

  /**
   * Update lives display
   */
  /** Current per-life energy as a 0..1 ratio (used by the boss scene's own HUD). */
  public getEnergyRatio(): number {
    return ENERGY_CONFIG.MAX_ENERGY > 0
      ? Math.max(0, Math.min(1, this.currentEnergy / ENERGY_CONFIG.MAX_ENERGY))
      : 0;
  }

  public updateLives(lives: number): void {
    const previousLives = this.currentLives;
    this.currentLives = lives;

    this.lifeStars.forEach((star, i) => {
      if (i < lives) {
        // Alive star - re-paint green and smash-pulse
        this.repaintLifeStar(star, true);
        star.setAngle(0);
        star.setAlpha(1);
        this.tweens.add({
          targets: star,
          scale: { from: star.scale * 1.4, to: 1 },
          duration: 300,
          ease: 'Back.easeOut',
        });
      } else if (i < previousLives) {
        // Star just lost - pop choreo, then drop into the empty state
        this.playStarBreakChoreo(star);
      } else {
        // Already empty - make sure the empty paint is in place
        this.repaintLifeStar(star, false);
        star.setAngle(0);
        star.setAlpha(1);
        star.setScale(1);
      }
    });
  }

  /**
   * Star-pop choreo when a life is lost. Flash, scale up, debris burst,
   * settle into the empty (navy) star.
   */
  private playStarBreakChoreo(star: Phaser.GameObjects.Graphics): void {
    this.tweens.killTweensOf(star);
    const sx = star.x;
    const sy = star.y;

    const heartSfx = this.cache.audio.exists('sfx_heart_shatter')
      ? 'sfx_heart_shatter' : 'sfx_heart_lost';
    AudioManager.getInstance().playSFX(this, heartSfx, { volume: 0.78 });

    // Red impact flash
    const flash = this.add.circle(sx, sy, 28, 0xFF0000, 0.8)
      .setBlendMode(Phaser.BlendModes.ADD).setDepth(200);
    this.tweens.add({
      targets: flash, scale: 3, alpha: 0, duration: 280, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });

    // Shockwave ring
    const ring = this.add.circle(sx, sy, 18, 0x000000, 0)
      .setStrokeStyle(4, 0xFF3355, 0.9).setDepth(201);
    this.tweens.add({
      targets: ring, scale: 4, alpha: 0, duration: 420, ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });

    // Green chunk debris
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 / 8) * i + Math.random() * 0.4;
      const speed = 50 + Math.random() * 90;
      const chunk = this.add.rectangle(
        sx, sy,
        4 + Math.random() * 4,
        4 + Math.random() * 4,
        HUD_GREEN,
      );
      chunk.setDepth(202).setAngle(Math.random() * 360);
      this.tweens.add({
        targets: chunk,
        x: sx + Math.cos(angle) * speed,
        y: sy + Math.sin(angle) * speed + 40,
        angle: chunk.angle + 360 * (Math.random() > 0.5 ? 1 : -1),
        alpha: 0,
        scale: 0,
        duration: 500 + Math.random() * 200,
        ease: 'Cubic.easeOut',
        onComplete: () => chunk.destroy(),
      });
    }

    // Star itself - pop up, crunch down, reappear empty
    this.tweens.add({
      targets: star,
      y: sy - 12,
      scale: 1.3,
      duration: 90,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: star,
          y: sy,
          scale: 0.15,
          angle: (Math.random() - 0.5) * 40,
          alpha: 0.2,
          duration: 220,
          ease: 'Back.easeIn',
          onComplete: () => {
            this.repaintLifeStar(star, false);
            star.setAngle(0);
            star.setAlpha(1);
            this.tweens.add({
              targets: star,
              scale: 1,
              duration: 260,
              ease: 'Back.easeOut',
              easeParams: [2],
            });
          },
        });
      },
    });

    // Screen-edge red pulse
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const pulse = this.add.graphics().setDepth(9998);
    pulse.fillStyle(0xFF0000, 0.25);
    pulse.fillRect(0, 0, WIDTH, 80);
    pulse.fillRect(0, HEIGHT - 80, WIDTH, 80);
    pulse.fillRect(0, 0, 80, HEIGHT);
    pulse.fillRect(WIDTH - 80, 0, 80, HEIGHT);
    pulse.setAlpha(0);
    this.tweens.add({
      targets: pulse,
      alpha: { from: 0, to: 1 },
      duration: 120,
      yoyo: true,
      hold: 60,
      ease: 'Sine.easeOut',
      onComplete: () => pulse.destroy(),
    });
  }

  /**
   * AAA v2: POSEIDON'S WRATH - full cinematic ultimate moment.
   *
   *   1. Sky-falling gold trident with motion-blur streak
   *   2. Radial concentric shockwaves (4) in sapphire/gold/pearl/coral
   *   3. Screen-wide lightning fork flickers
   *   4. Gold time-slice (500ms @ 0.25x) via gameplay scene
   *   5. Camera shake HUGE + zoom punch + gold flash + bloom ult
   *   6. 3-stop gradient text (cyan→gold→orange) with split-word reveal
   *   7. Radial chromatic aberration pulse 0→12→0 via CinematicPipeline
   *   8. Audio: sfx_wrath + pitched-down rumble + heavy low-freq layer
   */
  private triggerComboFinisher(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;

    const gameScene = this.scene.get(SCENE_KEYS.GAME);
    const audioManager = AudioManager.getInstance();

    // ── 1. CAMERA CINEMATIC (shake, zoom punch, flash, time-slice) ──
    if (gameScene?.cameras?.main) {
      const cam = gameScene.cameras.main;
      cam.flash(280, 255, 215, 0, false);
      cam.shake(520, 0.008);
      cam.zoomTo(1.08, 220, 'Back.easeOut');
      this.time.delayedCall(620, () => cam.zoomTo(1, 480, 'Quad.easeOut'));
      // GOLD TIME-SLICE - 500ms @ 0.25x for full ult impact
      gameScene.time.timeScale = 0.25;
      this.time.delayedCall(500, () => { gameScene.time.timeScale = 1; });
    }

    // ── 2. POSTFX ULT FLASH (bloom spike + chromatic ramp) ──────────
    try {
      const mgr = PostFXManager.get(gameScene ?? this);
      mgr?.ultFlash(900);
    } catch {}

    // ── 3. DIM BACKDROP (dark overlay so the text punches) ──────────
    const backdrop = this.add.rectangle(0, 0, WIDTH, HEIGHT, 0x050912, 0).setOrigin(0, 0).setDepth(9995);
    this.tweens.add({ targets: backdrop, alpha: 0.45, duration: 160, yoyo: true, hold: 700, ease: 'Quad.easeOut', onComplete: () => backdrop.destroy() });

    // ── 4. FALLING TRIDENT (sky → impact at frame center) ───────────
    const tridentGlyph = '\u2638'; // fallback symbol; overlaid behind the real sprite if available
    const trident = this.add.text(cx, -200, tridentGlyph, {
      fontFamily: 'CF Asty Std',
      fontSize: '400px',
      color: '#FFD700',
      stroke: '#3D1E00',
      strokeThickness: 16,
      shadow: { offsetX: 0, offsetY: 12, color: '#FFB000', blur: 40, fill: true },
    }).setOrigin(0.5).setDepth(10001).setAlpha(0.9);
    this.tweens.add({
      targets: trident,
      y: cy - 40,
      scale: { from: 0.6, to: 1.1 },
      duration: 240,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        // Impact: flash + shake intensifies + trident fades
        if (gameScene?.cameras?.main) gameScene.cameras.main.shake(300, 0.012);
        this.tweens.add({ targets: trident, scale: 1.8, alpha: 0, duration: 420, ease: 'Cubic.easeOut', onComplete: () => trident.destroy() });
      },
    });

    // ── 5. RADIAL SHOCKWAVE RINGS (4 color-graded) ──────────────────
    const ringColors = [0xFFD700, 0x00F0FF, 0xFFF8E8, 0xFF3B3B];
    ringColors.forEach((color, i) => {
      this.time.delayedCall(200 + i * 110, () => {
        const ring = this.add.circle(cx, cy, 80, 0x000000, 0).setStrokeStyle(10, color, 1);
        ring.setDepth(9999).setBlendMode(Phaser.BlendModes.ADD);
        this.tweens.add({
          targets: ring,
          scale: 22,
          alpha: 0,
          duration: 1000,
          ease: 'Cubic.easeOut',
          onComplete: () => ring.destroy(),
        });
      });
    });

    // ── 6. LIGHTNING FORKS (4 jagged lines flicker across screen) ──
    this.time.delayedCall(240, () => {
      const lightning = this.add.graphics().setDepth(10000).setBlendMode(Phaser.BlendModes.ADD);
      lightning.lineStyle(6, 0xFFFFFF, 1);
      for (let i = 0; i < 4; i++) {
        const startX = Math.random() * WIDTH;
        const endX = Math.random() * WIDTH;
        lightning.beginPath();
        lightning.moveTo(startX, 0);
        let px = startX;
        for (let y = 0; y < HEIGHT; y += 40) {
          px += (Math.random() - 0.5) * 90;
          lightning.lineTo(px, y);
        }
        lightning.lineTo(endX, HEIGHT);
        lightning.strokePath();
      }
      this.tweens.add({
        targets: lightning,
        alpha: 0,
        duration: 140,
        yoyo: true,
        repeat: 2,
        onComplete: () => lightning.destroy(),
      });
    });

    // ── 7. GRADIENT "POSEIDON'S WRATH" TEXT ─────────────────────────
    const wrathGlow = this.add.text(cx, cy - 20, "POSEIDON'S WRATH", {
      fontFamily: 'CF Asty Std',
      fontSize: '180px',
      color: '#FFD700',
      stroke: '#FFD700',
      strokeThickness: 28,
      fontStyle: 'italic',
    }).setOrigin(0.5).setDepth(10002).setAlpha(0).setBlendMode(Phaser.BlendModes.ADD);

    const wrathMain = this.add.text(cx, cy - 20, "POSEIDON'S WRATH", {
      fontFamily: 'CF Asty Std',
      fontSize: '180px',
      color: '#FFF4C2',
      stroke: '#3D1E00',
      strokeThickness: 16,
      fontStyle: 'italic',
      shadow: { offsetX: 0, offsetY: 10, color: '#000000', blur: 30, fill: true },
    } as any).setOrigin(0.5).setDepth(10003).setAlpha(0).setScale(0.2);

    try {
      const c = wrathMain.context as CanvasRenderingContext2D;
      const grad = c.createLinearGradient(0, 0, 0, wrathMain.height);
      grad.addColorStop(0, '#00F0FF');
      grad.addColorStop(0.45, '#FFF4C2');
      grad.addColorStop(0.6, '#FFD700');
      grad.addColorStop(1, '#EA580C');
      wrathMain.setTint(0xFFFFFF);
      (wrathMain.style as any).setFill(grad);
      wrathMain.updateText();
    } catch {}

    // Entrance - delay to sync with trident impact
    this.time.delayedCall(220, () => {
      this.tweens.add({ targets: [wrathMain, wrathGlow], alpha: 1, scale: 1.05, duration: 260, ease: 'Back.easeOut', easeParams: [3.5] });
      this.tweens.add({ targets: wrathGlow, alpha: { from: 1, to: 0.2 }, duration: 220, yoyo: true, repeat: 4, delay: 260, ease: 'Sine.easeInOut' });
    });

    // ── 8. SUB-TEXT ("x8 COMBO FINISHER") ───────────────────────────
    const sub = this.add.text(cx, cy + 110, 'ULTIMATE UNLEASHED', {
      fontFamily: 'CF Asty Std',
      fontSize: '68px',
      color: '#FFF8E8',
      stroke: '#3D1E00',
      strokeThickness: 8,
      fontStyle: 'italic',
      shadow: { offsetX: 0, offsetY: 6, color: '#000000', blur: 18, fill: true },
      letterSpacing: 6,
    } as any).setOrigin(0.5).setDepth(10003).setAlpha(0).setScale(0.3);
    this.time.delayedCall(380, () => {
      this.tweens.add({ targets: sub, alpha: 1, scale: 1, duration: 280, ease: 'Back.easeOut', easeParams: [2.5] });
    });

    // ── 9. PARTICLE STORM (layered gold + pearl + coral bursts) ─────
    try {
      const pp = ParticlePools.get(this);
      for (let i = 0; i < 6; i++) {
        this.time.delayedCall(180 + i * 80, () => {
          pp.burst('gold', cx + (Math.random() - 0.5) * 400, cy + (Math.random() - 0.5) * 220, 40);
          pp.burst('pearl', cx + (Math.random() - 0.5) * 600, cy + (Math.random() - 0.5) * 300, 24);
          if (i % 2 === 0) pp.burst('coral', cx + (Math.random() - 0.5) * 500, cy, 22);
        });
      }
    } catch {}

    // ── 10. AUDIO LAYERED STACK ─────────────────────────────────────
    try { audioManager.playSFX(this, 'sfx_wrath', { volume: 0.95 } as any); } catch {}
    try { audioManager.playSFX(this, 'sfx_boss_phase', { volume: 0.65, detune: -900 } as any); } catch {}
    try { audioManager.playSFX(this, 'sfx_splash', { volume: 0.7, detune: -1200 } as any); } catch {}
    try { audioManager.playSFX(this, 'stinger_perfect', { volume: 0.85 } as any); } catch {}
    // Oceanic Overwatch: Poseidon battle cry on the peak
    try { audioManager.playVoiceStinger(this, 'wrath', 2000); } catch {}

    // ── 11. EXIT ────────────────────────────────────────────────────
    this.time.delayedCall(1800, () => {
      this.tweens.add({
        targets: [wrathMain, wrathGlow, sub],
        alpha: 0,
        scale: 1.5,
        y: '-=80',
        duration: 520,
        ease: 'Power2',
        onComplete: () => { wrathMain.destroy(); wrathGlow.destroy(); sub.destroy(); },
      });
    });

    console.log('🌊⚡ POSEIDON\'S WRATH ACTIVATED! x8+ COMBO ULTIMATE!');
  }

  /**
   * Reset score to zero
   */
  public resetScore(): void {
    this.score = 0;
    this.resetStreak();
    this.updateScoreDisplay();
    console.log('🔄 Score reset');
  }

  /** Directly set the score (used by BossScene which manages its own scoring) */
  public setScore(newScore: number): void {
    this.score = newScore;
    this.updateScoreDisplay();
  }

  private updateScoreDisplay(): void {
    if (!this.scoreText) return;

    const target = this.score;
    const start = this.displayedScore;
    const diff = target - start;
    if (diff <= 0) {
      this.displayedScore = target;
      this.scoreText.setText(target.toLocaleString('en-US'));
      return;
    }

    // For big score jumps, duck music briefly for drama
    const choreo = AudioChoreographer.get(this);
    if (diff >= 500) {
      choreo.duck(0.55, 150, 300);
    }

    // TRIPLE-A: Juicy count-up via NumberRoller
    // Pulse scale on each tick, gold particle bursts at milestones, final thunk
    const durationMs = Math.min(900, 400 + Math.sqrt(diff) * 12);
    if (this.scoreRoller) {
      this.scoreRoller.rollTo(start, target, {
        duration: durationMs,
        ease: 'Back.easeOut',
        tickPulseScale: 1.08,
        milestone: 1000,
        showFloater: diff >= 500, // only for meaningful jumps
      });
      this.displayedScore = target;
    } else {
      // Fallback to the old choreographer if roller not initialized
      choreo.tickUpScore(
        start,
        target,
        (value) => {
          this.displayedScore = value;
          if (this.scoreText) {
            this.scoreText.setText(value.toLocaleString('en-US'));
          }
        },
        durationMs,
      );
    }
  }

  /**
   * Update ball counter display
   */
  public updateBallCount(remaining: number): void {
    if (this.hudLayout !== 'standard') return;
    if (this.ballCountText) {
      if (remaining > 0) {
        // Just the number - the "BUBBLES" label sits above it
        this.ballCountText.setText(remaining.toString());
        this.ballCountText.setAlpha(1);
      } else {
        this.ballCountText.setText('');
      }
    }
  }

  /**
   * Called once by GameScene when a stage with an educational itemTheme is
   * loaded. Creates the "BAGS CLEARED" secondary counter below the ball
   * count and remembers the item label (e.g. "bags", "nets") so subsequent
   * updateBagsCleared() calls can render correctly. Safe to call multiple
   * times - the second call re-uses the existing text nodes.
   */
  public setEducationalTheme(itemLabel: string): void {
    if (this.hudLayout !== 'standard') return;
    if (!itemLabel) return;
    this.educationalItemLabel = itemLabel.toUpperCase();

    if (this.bagsClearedText && this.bagsClearedLabel) {
      // Already built for a prior stage - reset count to 0 on new stage.
      this.bagsClearedText.setText('0');
      this.bagsClearedLabel.setText(`${this.educationalItemLabel} CLEARED`);
      return;
    }

    const { WIDTH } = GAME_CONFIG;
    // Match the BUBBLES pill positioning in create() (chunky-cartoon HUD).
    const BUBBLES_PILL_W = 220;
    const PILL_W = 280; // bag-cleared pill matches the TIME block width
    const PILL_RIGHT_X = WIDTH - 36 - BUBBLES_PILL_W; // align with bubbles pill right edge
    const PILL_LEFT_X = PILL_RIGHT_X + BUBBLES_PILL_W - PILL_W;
    // Sit one row below the TIME bar so it never crowds the main HUD.
    const pillY = 22 + 115 + 30 + 36; // ≈ 203

    const pillBg = this.add.graphics().setDepth(100);
    pillBg.fillStyle(HUD_NAVY_DEEP, 0.9);
    pillBg.fillRoundedRect(PILL_LEFT_X, pillY - 16, PILL_W, 32, 16);
    pillBg.lineStyle(3, HUD_NAVY, 1);
    pillBg.strokeRoundedRect(PILL_LEFT_X, pillY - 16, PILL_W, 32, 16);

    this.bagsClearedText = this.add.text(PILL_LEFT_X + 18, pillY, '0', {
      fontFamily: FONTS.display,
      fontSize: '22px',
      color: '#7CE89A',
      stroke: HUD_NAVY_HEX,
      strokeThickness: 3,
    } as any).setOrigin(0, 0.5).setDepth(101);

    this.bagsClearedLabel = this.add.text(PILL_LEFT_X + 56, pillY, `${this.educationalItemLabel} CLEARED`, {
      fontFamily: FONTS.display,
      fontSize: '14px',
      color: '#FFFFFF',
      stroke: HUD_NAVY_HEX,
      strokeThickness: 3,
      letterSpacing: 2,
    } as any).setOrigin(0, 0.5).setDepth(101);

    // Hide the whole row until the player has cleared at least one item.
    pillBg.setVisible(false);
    this.bagsClearedText.setVisible(false);
    this.bagsClearedLabel.setVisible(false);
    (this as any)._bagsClearedPillBg = pillBg;
  }

  /**
   * Increment the displayed "bags cleared" counter. Called by GameScene from
   * handleBallHit() whenever a bubble carrying an educational item pops.
   */
  public updateBagsCleared(count: number): void {
    if (this.hudLayout !== 'standard') return;
    if (!this.bagsClearedText) return;
    // Reveal the pill the first time a bag is cleared.
    if (count > 0) {
      const bg = (this as any)._bagsClearedPillBg as Phaser.GameObjects.Graphics | undefined;
      bg?.setVisible(true);
      this.bagsClearedText.setVisible(true);
      this.bagsClearedLabel?.setVisible(true);
    }
    this.bagsClearedText.setText(count.toString());
    // Small pop scale effect for feedback
    this.bagsClearedText.setScale(1.25);
    this.tweens.add({
      targets: this.bagsClearedText,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
    });
  }

  /**
   * Visual feedback for score change - shake effect scales with points earned
   * Small points (50-200): Subtle shake
   * Medium points (400-800): Moderate shake
   * Big points (1600+): Strong shake
   */
  private scoreVisualFeedback(pointsEarned: number): void {
    if (!this.scoreText) return;

    let scaleBump = 1.05;
    let duration = 200;

    if (pointsEarned >= 1600) {
      scaleBump = 1.2;
      duration = 300;
    } else if (pointsEarned >= 800) {
      scaleBump = 1.15;
      duration = 250;
    } else if (pointsEarned >= 400) {
      scaleBump = 1.1;
      duration = 225;
    }

    // Scale pulse on score text
    this.tweens.add({
      targets: this.scoreText,
      scale: scaleBump,
      duration: duration / 2,
      yoyo: true,
      ease: 'Back.easeOut',
    });

    // Flash effect on score text for big scores (with delay)
    if (pointsEarned >= 800) {
      this.tweens.add({
        targets: this.scoreText,
        alpha: 0.5,
        duration: 100,
        yoyo: true,
        repeat: 1,
        delay: 50, // Flash starts after movement begins
      });
    }
  }

  /**
   * Paint a chunky cartoon yellow pill with a subtle deeper-yellow rim
   * (no hard navy stroke - just yellow on yellow with a soft drop shadow).
   */
  private drawChunkyPill(x: number, y: number, w: number, h: number, r: number): void {
    const g = this.add.graphics().setDepth(99);

    // Drop shadow - moderate depth, "sticker on the canvas" feel
    g.fillStyle(0x000000, 0.40);
    g.fillRoundedRect(x + 4, y + 8, w, h, r);

    // Subtle bottom darker band so the pill reads as 3D card, not a flat tile
    const BAND = 8;
    g.fillStyle(HUD_YELLOW_SHADOW, 1);
    g.fillRoundedRect(x, y, w, h, r);
    g.fillStyle(HUD_YELLOW, 1);
    g.fillRoundedRect(x, y, w, h - BAND, r);

    // Top inner highlight stripe - soft gloss across the top quarter
    g.fillStyle(HUD_YELLOW_LIGHT, 0.55);
    g.fillRoundedRect(x + 8, y + 6, w - 16, Math.max(6, (h - BAND) * 0.26), Math.max(2, r - 8));

    // Soft rim - thin deeper-yellow line, NOT a navy stroke
    g.lineStyle(2, HUD_YELLOW_SHADOW, 0.9);
    g.strokeRoundedRect(x, y, w, h, r);
  }

  /**
   * Re-render an existing life-star Graphics in either ALIVE (green) or
   * EMPTY (dark navy) state. Stores the size on the object so future
   * repaints can keep the same dimensions.
   */
  private repaintLifeStar(g: Phaser.GameObjects.Graphics, alive: boolean, size?: number): void {
    const px = (size ?? (g.getData('starSize') as number)) || 30;
    g.setData('starSize', px);
    g.clear();

    const points = 5;
    const outerR = px / 2;
    const innerR = outerR * 0.42;
    const step = Math.PI / points;

    const traceStar = (radiusOffset: number) => {
      g.beginPath();
      for (let i = 0; i < 2 * points; i++) {
        const r = (i % 2 === 0 ? outerR : innerR) + radiusOffset;
        const angle = i * step - Math.PI / 2;
        const x = r * Math.cos(angle);
        const y = r * Math.sin(angle);
        if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
      }
      g.closePath();
    };

    if (alive) {
      // Drop shadow underneath
      g.fillStyle(0x000000, 0.45);
      g.translateCanvas(0, 3);
      traceStar(0);
      g.fillPath();
      g.translateCanvas(0, -3);
      // Main green fill
      g.fillStyle(HUD_GREEN, 1);
      traceStar(0);
      g.fillPath();
      // Highlight slice on the upper half
      g.fillStyle(HUD_GREEN_LIGHT, 0.65);
      traceStar(-3);
      g.fillPath();
      // Thick navy outline
      g.lineStyle(3, HUD_NAVY, 1);
      traceStar(0);
      g.strokePath();
    } else {
      // Empty: dark navy fill with same outline
      g.fillStyle(HUD_NAVY_DEEP, 0.9);
      traceStar(0);
      g.fillPath();
      g.lineStyle(3, HUD_NAVY, 1);
      traceStar(0);
      g.strokePath();
    }
  }

  /**
   * Horizontal pill bar that lives under the BUBBLES pill. ratio = 1.0 → 0.0.
   * Light blue fill at full → amber → red as the timer drains.
   */
  private drawTimerBar(ratio: number): void {
    const x: number = (this as any)._timerBarX;
    const y: number = (this as any)._timerBarY;
    const w: number = (this as any)._timerBarW;
    const h: number = (this as any)._timerBarH;
    if (x === undefined || y === undefined) return;

    const top = y - h / 2;
    const cornerR = h / 2;

    if (this.timerRingBg) {
      this.timerRingBg.clear();
      this.timerRingBg.fillStyle(HUD_NAVY_DEEP, 1);
      this.timerRingBg.fillRoundedRect(x, top, w, h, cornerR);
      this.timerRingBg.lineStyle(4, HUD_NAVY, 1);
      this.timerRingBg.strokeRoundedRect(x, top, w, h, cornerR);
    }

    if (this.timerRingFill) {
      this.timerRingFill.clear();
      if (ratio > 0) {
        let fillColor: number = HUD_BLUE;
        let sheenColor: number = HUD_BLUE_LIGHT;
        if (ratio <= 0.15) { fillColor = COLORS.dangerRed; sheenColor = 0xFFE0E0; }
        else if (ratio <= 0.3) { fillColor = COLORS.warningAmber; sheenColor = 0xFFF4C2; }

        const innerPad = 3;
        const innerX = x + innerPad;
        const innerTop = top + innerPad;
        const innerH = h - innerPad * 2;
        const maxInnerW = w - innerPad * 2;
        const innerR = innerH / 2;
        const drawW = Math.max(innerH, maxInnerW * ratio);

        this.timerRingFill.fillStyle(fillColor, 1);
        this.timerRingFill.fillRoundedRect(innerX, innerTop, drawW, innerH, innerR);
        this.timerRingFill.fillStyle(sheenColor, 0.85);
        this.timerRingFill.fillRoundedRect(innerX + 4, innerTop + 2, Math.max(0, drawW - 8), Math.max(2, innerH * 0.32), innerR);
      }
    }
  }

  /**
   * Create a frosted glass panel Graphics drawn at local origin (for use inside Containers)
   */
  // @ts-ignore - kept for potential future use
  private createGlassPanelGraphics(w: number, h: number, radius: number = 12): Phaser.GameObjects.Graphics {
    const g = this.add.graphics();

    // Dark frosted glass fill
    g.fillStyle(0x0a1628, 0.7);
    g.fillRoundedRect(-w / 2, -h / 2, w, h, radius);

    // Subtle inner highlight (top edge)
    g.fillStyle(0xFFFFFF, 0.07);
    g.fillRoundedRect(-w / 2 + 2, -h / 2 + 2, w - 4, h / 3, { tl: radius - 2, tr: radius - 2, bl: 0, br: 0 });

    // Gold border
    g.lineStyle(2.5, 0xFFD700, 0.75);
    g.strokeRoundedRect(-w / 2, -h / 2, w, h, radius);

    // Outer subtle glow
    g.lineStyle(1, 0xFFD700, 0.12);
    g.strokeRoundedRect(-w / 2 - 2, -h / 2 - 2, w + 4, h + 4, radius + 2);

    return g;
  }

  /**
   * Start the countdown timer
   */
  private startTimer(): void {
    // The boss fight is NOT timed. It ends only by depleting the Hydra's HP
    // (victory) or losing all lives (BossScene.gameOver, "Defeated by Hydra").
    // The boss reuses this shared HUD, which historically started a 150s
    // countdown here; on a poor connection (laggy input + a flickering video
    // background) the player could not engage in time and the expiring timer
    // dumped them straight to GAME OVER ("Time over") seconds after the Hydra
    // appeared, with no fight. Never arm the countdown for the boss layout.
    if (this.hudLayout === 'boss') {
      console.log('⏱ Boss layout - countdown disabled (boss has no time-out defeat)');
      return;
    }

    // Countdown every second
    this.timerEvent = this.time.addEvent({
      delay: 1000,
      callback: this.updateTimer,
      callbackScope: this,
      loop: true,
    });
  }

  /**
   * Update timer countdown (counts in seconds, not mm:ss)
   */
  private updateTimer(): void {
    this.timeRemaining--;

    // Update timer bar
    const ratio = Math.max(0, this.timeRemaining / this.startingTimeLimit);
    this.drawTimerBar(ratio);

    // ── TRIPLE-AAA: Timer state post-fx pulse ──
    try {
      const gameScene = this.scene.get(SCENE_KEYS.GAME);
      const mgr = PostFXManager.get(gameScene ?? this);
      if (this.timeRemaining <= 5 && this.timeRemaining > 0) {
        // critical - crimson vignette + chromatic
        mgr?.setDangerHold(0.6);
        mgr?.pulseDanger(240, 0.9);
      } else if (this.timeRemaining <= 15) {
        mgr?.setDangerHold(0.15);
      } else {
        mgr?.setDangerHold(0);
      }
    } catch {}

    // Update timer display
    if (this.timerText) {
      this.timerText.setText(this.timeRemaining.toString());

      // Warning when time is low
      if (this.timeRemaining <= 15 && this.timeRemaining > 5) {
        this.timerText.setColor('#FF4400');
        // Pulse scale
        this.tweens.add({
          targets: this.timerText,
          scale: 1.15,
          duration: 200,
          yoyo: true,
          ease: 'Back.easeOut',
        });
      } else if (this.timeRemaining <= 5 && this.timeRemaining > 0) {
        this.timerText.setColor('#FF0000');
        // AAA: EXTREME urgency - shake + pulse + screen edge glow
        this.tweens.add({
          targets: this.timerText,
          scale: 1.35,
          duration: 120,
          yoyo: true,
          ease: 'Back.easeOut',
        });
        this.tweens.add({
          targets: this.timerText,
          x: this.timerText.x + (Math.random() - 0.5) * 12,
          duration: 40,
          yoyo: true,
          repeat: 3,
        });
        // Flash alpha
        this.tweens.add({
          targets: this.timerText,
          alpha: 0.2,
          duration: 150,
          yoyo: true,
        });

        // AAA: Emit urgency event to GameScene for screen edge glow
        const gameScene = this.scene.get(SCENE_KEYS.GAME);
        if (gameScene) {
          gameScene.events.emit('timer-urgency', this.timeRemaining);
        }
      }
    }

    // Time's up!
    if (this.timeRemaining <= 0) {
      this.onTimeUp();
    }
  }

  /**
   * Handle when timer reaches zero
   */
  private onTimeUp(): void {
    // Stop the timer
    if (this.timerEvent) {
      this.timerEvent.remove();
    }

    // Safety net: the boss layout never arms the countdown (see startTimer),
    // but should anything force timeRemaining to zero while the boss is up,
    // do NOT route the boss to GAME OVER. The boss has no time-out defeat.
    if (this.hudLayout === 'boss') return;

    // Design mode: ignore timer, just reset it
    if (designModeState.isDesignMode()) {
      console.log('⏰ Timer expired but Design Mode active - resetting to 99');
      this.resetTimer(99);
      return;
    }

    console.log('⏰ Time\'s up!');

    // Time-over is only meaningful while a normal stage is actually RUNNING.
    // scene.get() returns the GameScene instance even when that scene is
    // stopped or paused (it is registered for the whole app lifetime), so
    // without this guard a stray tick would detonate a game-over into
    // whatever is really on screen (stage-clear card, menu, attract demo).
    const gameScene = this.scene.get(SCENE_KEYS.GAME) as any;
    if (!gameScene || !this.scene.isActive(SCENE_KEYS.GAME)) return;
    // Stage already resolved this frame (cleared or died): don't double-fire.
    if (gameScene.isGameOver) return;

    // Check if GameScene is in auto-play mode
    if (gameScene.isAutoPlay) {
      // Auto-play: trigger game over through GameScene (which handles restart)
      if (gameScene.gameOver) {
        gameScene.gameOver('Time over');
      }
      return;
    }

    // Normal mode: pause and show game over
    gameScene.scene.pause();
    this.scene.pause();

    // Use setTimeout since scene timers don't fire when paused
    setTimeout(() => {
      try {
        this.scene.start(SCENE_KEYS.GAME_OVER, { score: this.score, reason: 'Time over' });
      } catch (e) {
        console.error('Failed to start game over scene:', e);
      }
    }, 500);

    console.log('🎮 Game Over - Time expired');
  }

  /**
   * Reset timer to default value
   */
  public resetTimer(seconds: number = 99): void {
    this.startingTimeLimit = seconds;
    this.timeRemaining = seconds;
    if (this.timerText) {
      this.timerText.setText(this.timeRemaining.toString());
      this.timerText.setColor(COLORS_HEX.textPrimary);
    }
    this.drawTimerBar(1);
  }

  shutdown(): void {
    // Clean up event listeners when scene stops
    const gameScene = this.scene.get(SCENE_KEYS.GAME);
    if (gameScene) {
      gameScene.events.off('ball-destroyed', this.onBallDestroyed, this);
    }

    // Stop timer event
    if (this.timerEvent) {
      this.timerEvent.remove();
      this.timerEvent = undefined;
    }

    // Reset all state variables to ensure clean restart
    this.score = 0;
    this.currentStreakSize = null;
    this.streakCount = 0;
    this.startingTimeLimit = 99;
    this.timeRemaining = 99;

    // Clear stale Text references - Phaser destroys the display list on
    // shutdown, but our class fields still point at the destroyed Text
    // objects. If we leave them set, setEducationalTheme() on the next
    // stage's UIScene re-uses the dead Text and setText() throws inside
    // Phaser's canvas drawImage - which propagates out of the ball-pop
    // tween's onStart and freezes the scene mid-split.
    this.bagsClearedText = undefined;
    this.bagsClearedLabel = undefined;

    console.log('📊 UI scene shutdown - cleaned up and reset state');
  }
}
